// @flow

import React, { useContext } from 'react';
import { useFormikContext } from 'formik';
import { useParams } from 'react-router-dom';
import { useGlobalDiscountProvidedByStatus } from '@src/custom-hooks/useGlobalDiscountProvidedByStatus';
import { getIfAnyCommittedCapacityLineItemsAreConfigured } from '@components/committed-capacity/utils';

import { PaddedAndRaisedSegment } from '../presentational/PaddedAndRaisedSegment';
import {
  extractRelevantFieldValuesFromForm,
  getEditedFieldNamesBasedOnFormikValuesAndInitialValues,
  isFormEditedBasedOnCurrentValues,
  isGlobalDiscountEdited,
  resetFormValuesToInitialValues,
} from '../../common-utils/utils';
import { SaveFormButton } from '../presentational/buttons/SaveFormButton';
import { Spacer } from '../presentational/spacing/Spacer';
import { useUpdateEstimateMutation } from '../../service-definitions/estimates';
import { EstimateContext } from '../../contexts/EstimateContext';
import { ResetFormButton } from '../presentational/buttons/ResetFormButton';
import { toastError } from '../presentational/notifications/utils';
import { ESTIMATE_COMMIT_DETAILS_CONFIG } from '../../configuration/estimate-commit-details';
import { isAnythingEditedOnEstimatePageBesidesTheGivenForm } from '../views/utils';
import {
  ESTIMATE_COMMIT_DETAILS_FORM,
  isCommittedCapacityLiveForConnectOrTF,
} from '../../constants';
import { APPLY_SYSTEM_GENERATED_CNBC_BACKEND_NAME } from '../common/sgcnbc/constants';

import { EstimateCommitDetailsGrid } from './EstimateCommitDetailsGrid';

const getConfirmationConfigsForSave = (values, initialValues, isGlobalDiscountSystemGenerated) => {
  const confirmationConfigs = [];
  const isGDEdited = isGlobalDiscountEdited(values, initialValues);

  const shouldShowSGConfirmation = isGlobalDiscountSystemGenerated && isGDEdited;

  const shouldShowCCConfirmation =
    isCommittedCapacityLiveForConnectOrTF() &&
    isGDEdited &&
    getIfAnyCommittedCapacityLineItemsAreConfigured(values);

  if (shouldShowSGConfirmation) {
    confirmationConfigs.push({
      header: 'Edit System-Generated Global Discount',
      body: 'If you proceed, then SG discount will no longer apply. Are you sure you want to proceed?',
      okButtonText: 'Yes, proceed',
      cancelButtonText: 'Cancel',
    });
  }

  if (shouldShowCCConfirmation) {
    confirmationConfigs.push({
      header: 'Committed Capacity Review Required',
      body: 'Changing the global discount may affect the effective discount for committed capacity. Please review your committed capacity configuration after saving. Non-admin rows will be auto-adjusted based on the new effective discount and admin rows will be untouched. However, a warning icon appears wherever the entered discount falls outside of the permitted range!',
      okButtonText: 'Yes, I understand!!',
      cancelButtonText: 'Cancel',
    });
  }

  return confirmationConfigs;
};

export const EstimateCommitDetailsContainer = () => {
  const { estimateId } = useParams();
  const estimate = useContext(EstimateContext);
  const { values, isValid, initialValues, resetForm } = useFormikContext();
  const [updateEstimateDetails] = useUpdateEstimateMutation();
  const { isGlobalDiscountSystemGenerated } = useGlobalDiscountProvidedByStatus();

  const confirmationConfigs = getConfirmationConfigsForSave(
    values,
    initialValues,
    isGlobalDiscountSystemGenerated
  );

  const shouldSaveBeDisabled =
    !isValid ||
    !isFormEditedBasedOnCurrentValues(values, initialValues, ESTIMATE_COMMIT_DETAILS_CONFIG);

  const shouldResetBeDisabled = !isFormEditedBasedOnCurrentValues(
    values,
    initialValues,
    ESTIMATE_COMMIT_DETAILS_CONFIG
  );

  const shouldFormBeDisabled = isAnythingEditedOnEstimatePageBesidesTheGivenForm(
    values,
    initialValues,
    ESTIMATE_COMMIT_DETAILS_FORM
  );

  return (
    <PaddedAndRaisedSegment disabled={shouldFormBeDisabled}>
      <EstimateCommitDetailsGrid />
      <Spacer y={40} />

      <ResetFormButton
        disabled={shouldResetBeDisabled}
        onClick={() => {
          resetFormValuesToInitialValues(
            values,
            ESTIMATE_COMMIT_DETAILS_CONFIG,
            initialValues,
            resetForm
          );
        }}
      />

      <Spacer x={1} />

      <SaveFormButton
        confirmationConfigs={confirmationConfigs}
        disabled={shouldSaveBeDisabled}
        onClick={async () => {
          const payload = extractRelevantFieldValuesFromForm(
            ESTIMATE_COMMIT_DETAILS_CONFIG,
            values
          );
          payload.modified_fields = getEditedFieldNamesBasedOnFormikValuesAndInitialValues(
            values,
            initialValues,
            ESTIMATE_COMMIT_DETAILS_CONFIG
          );
          payload.estimate_version = estimate?.inputs?.estimate_version;

          // When net_new_customer is disabled, also disable sg_cnbc_enabled
          if (payload.net_new_customer === false) {
            payload[APPLY_SYSTEM_GENERATED_CNBC_BACKEND_NAME] = false;
            payload.modified_fields.push(APPLY_SYSTEM_GENERATED_CNBC_BACKEND_NAME);
          }

          const { error } = await updateEstimateDetails({
            estimateId,
            payload,
          });
          if (error) {
            toastError(error);
          }
        }}
        shouldShowConfirmation={confirmationConfigs.length > 0}
      />
    </PaddedAndRaisedSegment>
  );
};
